import React, { useEffect, useState, useMemo } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { fetchTrustHubDashboard } from '../../../store/trustHubSlice';
import { setPageTitle, toggleNavigation, toggleBreadcrum } from '../../../store/Page';
import { TabNavigation, LoadingSkeleton } from './components/shared';
import OverviewTab from './components/overview/OverviewTab';
import BusinessProfileTab from './tabs/BusinessProfileTab';
import PhoneInventoryTab from './tabs/PhoneInventoryTab';
import VoiceComplianceTab from './tabs/VoiceComplianceTab';
import SmsComplianceTab from './tabs/SmsComplianceTab';
import VoipComplianceTab from './tabs/VoipComplianceTab';
import './TrustHubDashboard.css';

const TrustHubDashboard = ({ user }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const { dashboardData, dashboardLoading, dashboardError, profile } = useSelector(
    (state) => state.trustHub
  );

  // Check if profile is approved (handles 'approved', 'twilio-approved', 'verified')
  const isProfileApproved = profile?.status &&
    ['approved', 'twilio-approved', 'verified'].includes(profile.status);

  // Get active tab from query params or default to 'overview'
  const getActiveTabFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab') || 'overview';

    // Handle tab aliases - redirect specific feature tabs to their parent tabs
    const tabAliases = {
      'cnam': 'voice',
      'shaken-stir': 'voice',
      'stir-shaken': 'voice',
      'voice-integrity': 'voice',
    };

    return tabAliases[tab] || tab;
  };

  const [activeTab, setActiveTab] = useState(getActiveTabFromUrl());

  // Define tabs configuration - memoized to recalculate when profile approval status changes
  const tabs = useMemo(() => [
    {
      key: 'overview',
      label: 'Overview',
    },
    {
      key: 'profile',
      label: 'Business Profile',
    },
    {
      key: 'voice',
      label: 'Voice Compliance',
      disabled: !isProfileApproved,
    },
    {
      key: 'sms',
      label: 'SMS Compliance',
      disabled: !isProfileApproved,
    },
    {
      key: 'phones',
      label: 'Phone Number Inventory',
      disabled: !isProfileApproved,
    },
    {
      key: 'voip',
      label: 'VoIP',
       disabled: !isProfileApproved,
    },
  ], [isProfileApproved]);

  // Set page configuration on mount
  useEffect(() => {
    dispatch(setPageTitle({ title: "Business & Compliance", subHeaderTitle: "Business Profile & Compliance Center" }));
    dispatch(toggleNavigation({ toggle: true }));
    dispatch(toggleBreadcrum({ toggle: false }));
  }, [dispatch]);

  // Update active tab when URL changes
  useEffect(() => {
    const tab = getActiveTabFromUrl();

    // Only check disabled state if profile data is loaded
    // This prevents redirecting before we know the actual approval status
    if (profile) {
      const targetTab = tabs.find(t => t.key === tab);
      if (targetTab?.disabled) {
        // Redirect to profile tab if trying to access disabled tab via URL
        history.replace('/trust-hub?tab=profile');
        setActiveTab('profile');
        return;
      }
    }

    setActiveTab(tab);
  }, [location.search, isProfileApproved, profile, tabs, history]);

  // Fetch dashboard data on mount
  useEffect(() => {
    console.log('TrustHub - User object:', user);
    console.log('TrustHub - User ID:', user?.user_id);

    if (user && user.user_id) {
      console.log('TrustHub - Fetching dashboard for user:', user.user_id);
      dispatch(fetchTrustHubDashboard(user.user_id));
    } else {
      console.warn('TrustHub - No user_id found. User object:', user);
    }
  }, [dispatch, user]);

  // Handle tab change
  const handleTabChange = (tabKey) => {
    // Check if the tab is disabled
    const targetTab = tabs.find(tab => tab.key === tabKey);
    if (targetTab?.disabled) {
      // Redirect to profile tab if trying to access disabled tab
      history.push(`/trust-hub?tab=profile`);
      return;
    }
    history.push(`/trust-hub?tab=${tabKey}`);
  };

  // Render tab content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab dashboardData={dashboardData} user={user} />;
      case 'profile':
        return <BusinessProfileTab user={user} />;
      case 'phones':
        return <PhoneInventoryTab user={user} />;
      case 'voice':
        return <VoiceComplianceTab user={user} />;
      case 'sms':
        return <SmsComplianceTab user={user} />;
      case 'voip':
        return <VoipComplianceTab />;
      default:
        return <OverviewTab dashboardData={dashboardData} user={user} />;
    }
  };

  // Get theme colors from customizedDashboard
  const getThemeColors = () => {
    if (!user || !user.customizedDashboard || !user.customizedDashboard[0]) {
      return {
        gradientColor: 'linear-gradient(to right, #087ab6, #00d39e)',
        primaryColor: '#087ab6',
        secondaryColor: '#00d39e',
        sidebarIconColor: '#087ab6',
      };
    }

    const theme = user.customizedDashboard[0];
    const headerColor = theme.header_color || '#087ab6';
    const secondaryHeaderColor = theme.secondary_header_color || '#00d39e';
    const sidebarIconColor = theme.sidebar_icon_color || '#087ab6';

    return {
      gradientColor: `linear-gradient(to right, ${headerColor}, ${secondaryHeaderColor})`,
      primaryColor: headerColor,
      secondaryColor: secondaryHeaderColor,
      sidebarIconColor: sidebarIconColor,
    };
  };

  const themeColors = getThemeColors();

  if (dashboardLoading) {
    return (
      <>
        <div id="trust-hub-dashboard" className="trust-hub-container">
          <div className="trust-hub-header">
            <h1>Business Profile & Compliance Center</h1>
            <p className="subtitle">Manage all communication compliance requirements</p>
          </div>
          <TabNavigation tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />
          <LoadingSkeleton type="dashboard" />
        </div>
        <style>
          {`#trust-hub-dashboard .linear-gradient, #trust-hub-dashboard .btn.linear-gradient, #trust-hub-dashboard .zy-save-btn { background: ${themeColors.gradientColor} !important; color: #fff !important; }`}
          {`#trust-hub-dashboard .tab-navigation__tab--active { color: ${themeColors.primaryColor} !important; border-bottom-color: ${themeColors.primaryColor} !important; }`}
          {`#trust-hub-dashboard .tab-navigation__tab:hover:not(.tab-navigation__tab--disabled) { color: ${themeColors.primaryColor} !important; }`}
          {`#trust-hub-dashboard .section-title { color: ${themeColors.primaryColor} !important; }`}
          {`#trust-hub-dashboard .section-title::before { background: ${themeColors.gradientColor} !important; }`}
          {`#trust-hub-dashboard .detail-item a { color: ${themeColors.primaryColor} !important; }`}
          {`#trust-hub-dashboard .detail-item a:hover { color: ${themeColors.secondaryColor} !important; }`}
          {`#trust-hub-dashboard .compliance-card__action--primary { background: ${themeColors.gradientColor} !important; }`}
          {`#trust-hub-dashboard .profile-status-title { color: ${themeColors.primaryColor} !important; }`}
          {`#trust-hub-dashboard .phone-summary-header h2 { color: ${themeColors.primaryColor} !important; }`}
        </style>
      </>
    );
  }

  if (dashboardError) {
    return (
      <>
        <div id="trust-hub-dashboard" className="trust-hub-container">
          <div className="alert alert-danger">
            <h4>Error Loading Dashboard</h4>
          
            <button
              className="btn linear-gradient zy-save-btn ml-2 mt-3"
              onClick={() => user && user.user_id && dispatch(fetchTrustHubDashboard(user.user_id))}
            >
              Retry
            </button>
          </div>
        </div>
        <style>
          {`#trust-hub-dashboard .linear-gradient, #trust-hub-dashboard .btn.linear-gradient, #trust-hub-dashboard .zy-save-btn { background: ${themeColors.gradientColor} !important; color: #fff !important; }`}
        </style>
      </>
    );
  }

  // Handle missing user data
  if (!user || !user.user_id) {
    return (
      <div id="trust-hub-dashboard" className="trust-hub-container">
        <div className="alert alert-warning">
          <h4>User Not Found</h4>
          <p>Unable to load Business dashboard. Please ensure you are logged in.</p>
          <p className="text-muted" style={{fontSize: '12px'}}>
            Debug: user={JSON.stringify(user)}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div id="trust-hub-dashboard" className="trust-hub-container">
        <div className="trust-hub-header">
          <h1>Business Profile & Compliance Center</h1>
          <p className="subtitle">Manage all communication compliance requirements</p>
        </div>

        <TabNavigation tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />

        {renderTabContent()}
      </div>
      <style>
        {`#trust-hub-dashboard .linear-gradient, #trust-hub-dashboard .btn.linear-gradient, #trust-hub-dashboard .zy-save-btn { background: ${themeColors.gradientColor} !important; color: #fff !important;margin-left:0 !important;height: 42px; }`}
        {`#trust-hub-dashboard .tab-navigation__tab--active { color: ${themeColors.primaryColor} !important; border-bottom-color: ${themeColors.primaryColor} !important; }`}
        {`#trust-hub-dashboard .tab-navigation__tab:hover:not(.tab-navigation__tab--disabled) { color: ${themeColors.primaryColor} !important; }`}
        {`#trust-hub-dashboard .tab-navigation__tab { color: ${themeColors.sidebarIconColor} !important; }`}
        {`#trust-hub-dashboard .tab-navigation__tab--active .tab-navigation__badge { background-color: ${themeColors.primaryColor} !important; }`}
        {`#trust-hub-dashboard .section-title { color: ${themeColors.primaryColor} !important; }`}
        {`#trust-hub-dashboard .section-title::before { background: ${themeColors.gradientColor} !important; }`}
        {`#trust-hub-dashboard .detail-item a { color: ${themeColors.primaryColor} !important; }`}
        {`#trust-hub-dashboard .detail-item a:hover { color: ${themeColors.secondaryColor} !important; }`}
        {`#trust-hub-dashboard .compliance-card__action--primary { background: ${themeColors.gradientColor} !important; }`}
        {`#trust-hub-dashboard .btn-primary { background: ${themeColors.gradientColor} !important; }`}
        {`#trust-hub-dashboard .pagination-controls .btn-sm:hover:not(:disabled) { border-color: ${themeColors.primaryColor}; color: ${themeColors.primaryColor}; }`}
        {`#trust-hub-dashboard .profile-status-title { color: ${themeColors.primaryColor} !important; }`}
        {`#trust-hub-dashboard .phone-summary-header h2 { color: ${themeColors.primaryColor} !important; }`}
      </style>
    </>
  );
};

const mapStateToProps = (state) => ({
  user: state.user.company,
});

export default connect(mapStateToProps)(TrustHubDashboard);
